import api from '../services/api';
import { formatMoney } from './format';

type RazorpayResponse = { razorpay_order_id: string; razorpay_payment_id: string; razorpay_signature: string };
type RazorpayInstance = { open: () => void; on: (event: string, cb: (r: { error?: { description?: string } }) => void) => void };

declare global {
  interface Window {
    Razorpay?: new (options: Record<string, unknown>) => RazorpayInstance;
  }
}

type Checkout = {
  gateway: 'razorpay' | 'sandbox';
  keyId: string | null;
  orderId: string;
  amount: number;
  currency: string;
  mode: 'DOMESTIC' | 'INTERNATIONAL';
  description: string;
  companyName: string;
  prefill: { name: string; email: string; contact: string };
};

const CHECKOUT_SRC = 'https://checkout.razorpay.com/v1/checkout.js';

function loadCheckout(): Promise<void> {
  if (window.Razorpay) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = CHECKOUT_SRC;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Could not load Razorpay checkout. Check your connection and try again.'));
    document.body.appendChild(script);
  });
}

/** Outside India only cards are offered — Razorpay's international card flow. */
const INTERNATIONAL_DISPLAY = {
  display: {
    hide: [{ method: 'upi' }, { method: 'netbanking' }, { method: 'wallet' }, { method: 'paylater' }, { method: 'emi' }, { method: 'cardless_emi' }],
    preferences: { show_default_blocks: true },
  },
};

/** Opens checkout for a Micus payment and verifies it server-side. */
export async function payNow(paymentId: string): Promise<'paid' | 'dismissed'> {
  const { data } = await api.post<{ checkout: Checkout }>(`/customer/payments/${paymentId}/checkout`);
  const c = data.checkout;
  const verify = (paymentRef: string, signature?: string) =>
    api.post(`/customer/payments/${paymentId}/verify`, { orderId: c.orderId, paymentId: paymentRef, signature: signature || null });

  if (c.gateway === 'sandbox') {
    const ok = window.confirm(`Sandbox payment — Razorpay keys are not configured yet.\n\nSimulate paying ${formatMoney(c.amount, c.currency)} for "${c.description}"?`);
    if (!ok) return 'dismissed';
    await verify(`sandbox_pay_${Date.now()}`);
    return 'paid';
  }

  await loadCheckout();
  return new Promise((resolve, reject) => {
    const rzp = new window.Razorpay!({
      key: c.keyId,
      order_id: c.orderId,
      amount: c.amount,
      currency: c.currency,
      name: 'Micus',
      description: c.description,
      prefill: c.prefill,
      notes: { company: c.companyName },
      theme: { color: '#d6006e' },
      ...(c.mode === 'INTERNATIONAL' ? { config: INTERNATIONAL_DISPLAY } : {}),
      handler: (r: RazorpayResponse) => {
        verify(r.razorpay_payment_id, r.razorpay_signature).then(() => resolve('paid'), reject);
      },
      modal: { ondismiss: () => resolve('dismissed') },
    });
    rzp.on('payment.failed', (r) => {
      void api.post(`/customer/payments/${paymentId}/failed`, { reason: r.error?.description || null }).catch(() => undefined);
    });
    rzp.open();
  });
}
